import Close from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import clsx from "clsx";
import type { ReactElement } from "react";
import { useToggle } from "../../hooks";

export type BannerProps = {
  dismissible?: boolean;
  onDismiss?: () => void;
  inline?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
  spacer?: boolean;
  placement?: "bottom" | "top";
  zIndex?: number;
} & React.HTMLAttributes<HTMLDivElement>;

export function Banner({
  dismissible,
  onDismiss,
  inline,
  fullWidth,
  children,
  style,
  spacer,
  placement = "top",
  zIndex = 1300,
  className,
  ...rest
}: BannerProps): ReactElement | null {
  const [internalDismissed, toggleDismissed] = useToggle(false);
  const dismissed = onDismiss !== undefined ? false : internalDismissed;

  if (!children || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss();
      return;
    }

    toggleDismissed(true)();
  };

  return (
    <>
      {spacer && !inline ? <div className="mt-[70px]" /> : null}
      <div
        {...rest}
        className={clsx(
          "flex bg-[var(--color-background--alt)]",
          inline
            ? clsx(
                "relative z-[1] box-border",
                fullWidth ? "w-full items-stretch" : "row f-c-c px-4",
                placement === "top"
                  ? "border-b border-[var(--color-divider)]"
                  : "border-t border-[var(--color-divider)]",
              )
            : clsx(
                "fixed left-0 right-0 px-4 border-[var(--color-divider)]",
                fullWidth ? "w-full items-stretch" : "items-center",
                placement === "top" ? "top-0 border-b" : "bottom-0 border-t",
              ),
          className,
        )}
        style={{
          ...(inline ? undefined : { zIndex }),
          ...style,
        }}
      >
        <div
          className={clsx(
            fullWidth && "min-w-0 flex-1",
            !(inline && fullWidth) && "min-h-[42px] py-4 text-base",
            inline && !fullWidth && "p-4",
          )}
        >
          {children}
        </div>
        {dismissible ? (
          <span className={clsx("shrink-0", fullWidth && "pr-2")}>
            <IconButton aria-label="Dismiss banner" onClick={handleDismiss}>
              <Close />
            </IconButton>
          </span>
        ) : null}
      </div>
    </>
  );
}
